import { calcularTiempoTrabajado } from "../interfaces/formik/IReporteRelojChecador.ts";
import type { PayrollVisitHourRow } from "./payrollVisitHours.ts";
import { parseTimeToMinutes } from "./payrollBranchMeta.ts";

export type ProgrammedSlot = { entrada: string; salida: string };

export type ProgramadoDisplayResult = ProgrammedSlot & {
  isVarios: boolean;
  /** Sin horario en agenda: programado = checada registrada. */
  usesRegisteredAsProgramado?: boolean;
};

function scheduleSlotKey(entrada: string, salida: string): string {
  return `${entrada}|${salida}`;
}

/** Horarios programados únicos (ignora filas de horario duplicadas en BD). */
export function uniqueProgrammedSlotsFromSchedules(
  schedules: Array<{ start?: string; end?: string; startTime?: string; endTime?: string }>
): ProgrammedSlot[] {
  const seen = new Set<string>();
  const slots: ProgrammedSlot[] = [];

  for (const schedule of schedules || []) {
    const startRaw = schedule.start ?? schedule.startTime ?? "";
    const endRaw = schedule.end ?? schedule.endTime ?? "";
    if (!startRaw || !endRaw) continue;

    const startPart = String(startRaw).includes(" - ")
      ? String(startRaw).split(" - ")[0]?.trim()
      : String(startRaw).trim();
    const endPart = String(endRaw).includes(" - ")
      ? String(endRaw).split(" - ")[1]?.trim() ||
        String(endRaw).split(" - ")[0]?.trim()
      : String(endRaw).trim();

    const entrada = startPart.includes(":")
      ? startPart.split(":").length >= 3
        ? startPart
        : `${startPart}:00`
      : `${startPart}:00`;
    const salida =
      endPart === "00:00" || endPart === "00:00:00"
        ? "24:00:00"
        : endPart.includes(":") && endPart.split(":").length >= 3
          ? endPart
          : `${endPart}:00`;

    const key = scheduleSlotKey(entrada, salida);
    if (seen.has(key)) continue;
    seen.add(key);
    slots.push({ entrada, salida });
  }

  return slots;
}

export function resolveProgramadoDisplayFromSchedules(
  schedules: Array<{ start?: string; end?: string; startTime?: string; endTime?: string }>
): ProgrammedSlot & { isVarios: boolean } {
  const slots = uniqueProgrammedSlotsFromSchedules(schedules);
  if (slots.length === 0) {
    return { entrada: "N/A", salida: "N/A", isVarios: false };
  }
  if (slots.length === 1) {
    return { ...slots[0], isVarios: false };
  }
  return { entrada: "Varios", salida: "Varios", isVarios: true };
}

/** Sin horario en BD: programado = mismas horas que la checada registrada (no programado). */
function programadoFromRegisteredVisits(
  visits: PayrollVisitHourRow[]
): ProgrammedSlot | null {
  const deduped = dedupePayrollVisitsByPair(visits);
  if (deduped.length === 0) {
    return null;
  }
  const sorted = [...deduped].sort((a, b) =>
    a.entradaRegistrada.localeCompare(b.entradaRegistrada)
  );
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first?.entradaRegistrada || !last?.salidaRegistrada) {
    return null;
  }
  return {
    entrada: first.entradaRegistrada,
    salida: last.salidaRegistrada,
  };
}

const EVENING_IN_MINUTES = 18 * 60;
const MORNING_SLOT_MAX_MINUTES = 12 * 60;

/**
 * Horario ligado a checada (no agenda real): entrada programada de mañana pero checada de noche.
 */
export function shouldPreferRegisteredProgramadoForNightShift(
  slots: ProgrammedSlot[],
  registered: ProgrammedSlot
): boolean {
  if (slots.length !== 1) {
    return false;
  }
  const slot = slots[0];
  const regIn = parseTimeToMinutes(registered.entrada);
  const slotIn = parseTimeToMinutes(slot.entrada);
  const regOut = parseTimeToMinutes(registered.salida);
  const slotOut = parseTimeToMinutes(slot.salida);

  if (regIn >= EVENING_IN_MINUTES && slotIn < MORNING_SLOT_MAX_MINUTES) {
    return true;
  }

  const inDiff = Math.abs(regIn - slotIn);
  const outDiff = Math.abs(regOut - slotOut);
  return outDiff <= 30 && inDiff >= 3 * 60;
}

/** Programado: "Varios" solo si hay varios cortes/sucursales en checadas (como TableChequeo). */
export function resolveProgramadoDisplayForPayrollDay(
  schedules: Array<{ start?: string; end?: string; startTime?: string; endTime?: string }>,
  visits: PayrollVisitHourRow[],
  options?: { nightScheduleEnabled?: boolean }
): ProgramadoDisplayResult {
  const slots = uniqueProgrammedSlotsFromSchedules(schedules);
  if (shouldShowVariosRegistros(visits)) {
    return { entrada: "Varios", salida: "Varios", isVarios: true };
  }

  const fromChecks = programadoFromRegisteredVisits(visits);
  const useRegistered =
    Boolean(fromChecks) &&
    (slots.length === 0 ||
      (options?.nightScheduleEnabled &&
        shouldPreferRegisteredProgramadoForNightShift(slots, fromChecks!)));

  if (useRegistered && fromChecks) {
    return { ...fromChecks, isVarios: false, usesRegisteredAsProgramado: true };
  }

  if (slots.length === 0) {
    return { entrada: "N/A", salida: "N/A", isVarios: false };
  }
  return { ...slots[0], isVarios: false };
}

export function isUnscheduledPayrollDay(
  schedules: Array<{ start?: string; end?: string; startTime?: string; endTime?: string }>,
  visits: PayrollVisitHourRow[]
): boolean {
  return (
    uniqueProgrammedSlotsFromSchedules(schedules).length === 0 &&
    dedupePayrollVisitsByPair(visits).length > 0
  );
}

/** Misma regla que RowReporteChequeo / TableChequeo: siempre desde entrada–salida del par. */
export function visitRegisteredHours(visit: PayrollVisitHourRow): number {
  const entrada = String(visit.entradaRegistrada || "").trim();
  const salida = String(visit.salidaRegistrada || "").trim();
  if (!entrada || !salida || entrada === "N/A" || salida === "N/A") {
    return 0;
  }
  return calcularTiempoTrabajado(entrada, salida);
}

/** Turno nocturno: un solo corte principal (no sumar pareos espurios de madrugada). */
export function pickPrimaryNightPayrollVisit(
  visits: PayrollVisitHourRow[]
): PayrollVisitHourRow | null {
  const deduped = dedupePayrollVisitsByPair(visits);
  if (deduped.length === 0) {
    return null;
  }
  if (deduped.length === 1) {
    return deduped[0];
  }

  const minEvening = EVENING_IN_MINUTES;
  const scored = deduped.map((visit) => {
    const inMin = parseTimeToMinutes(visit.entradaRegistrada);
    const hours = visitRegisteredHours(visit);
    return {
      visit,
      hours,
      inMin,
      isEvening: inMin >= minEvening,
    };
  });

  const evening = scored
    .filter((s) => s.isEvening)
    .sort((a, b) => b.hours - a.hours || b.inMin - a.inMin);
  if (evening.length > 0) {
    return evening[0].visit;
  }

  return scored.sort((a, b) => b.hours - a.hours || b.inMin - a.inMin)[0].visit;
}

/**
 * Horas del día como TableChequeo: un corte → calcularTiempoTrabajado del par;
 * varios cortes reales → suma de tramos.
 */
export function resolvePayrollDayRegisteredHours(
  visits: PayrollVisitHourRow[],
  options?: {
    entradaRegistrada?: string;
    salidaRegistrada?: string;
    nightScheduleEnabled?: boolean;
  }
): number {
  const deduped = dedupePayrollVisitsByPair(visits);

  if (shouldShowVariosRegistros(deduped)) {
    return deduped.reduce((sum, visit) => sum + visitRegisteredHours(visit), 0);
  }

  const entrada = String(options?.entradaRegistrada || "").trim();
  const salida = String(options?.salidaRegistrada || "").trim();
  if (entrada && salida && entrada !== "N/A" && salida !== "N/A") {
    return calcularTiempoTrabajado(entrada, salida);
  }

  if (deduped.length === 0) {
    return 0;
  }

  if (options?.nightScheduleEnabled) {
    const primary = pickPrimaryNightPayrollVisit(deduped);
    if (primary) {
      return visitRegisteredHours(primary);
    }
  }

  if (deduped.length === 1) {
    return visitRegisteredHours(deduped[0]);
  }

  return deduped.reduce((sum, visit) => sum + visitRegisteredHours(visit), 0);
}

/** No programado: horas en horario = horas registradas; sin extras por diferencia de turno. */
export function resolveUnscheduledPayrollDayHours(
  visits: PayrollVisitHourRow[],
  options?: {
    entradaRegistrada?: string;
    salidaRegistrada?: string;
    nightScheduleEnabled?: boolean;
  }
): {
  horasRegistradas: number;
  horasTrabajadasEnHorario: number;
  horasExtras: number;
} {
  const horasRegistradas = resolvePayrollDayRegisteredHours(visits, options);
  return {
    horasRegistradas,
    horasTrabajadasEnHorario: horasRegistradas,
    horasExtras: 0,
  };
}

/** Misma regla que TableChequeo: varios = varios cortes o varias sucursales, no checadas duplicadas. */
export function dedupePayrollVisitsByPair(
  visits: PayrollVisitHourRow[]
): PayrollVisitHourRow[] {
  const seen = new Set<string>();
  const out: PayrollVisitHourRow[] = [];
  for (const visit of visits || []) {
    const key = `${visit.entradaRegistrada}|${visit.salidaRegistrada}|${visit.branchId ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(visit);
  }
  return out;
}

export function shouldShowVariosRegistros(visits: PayrollVisitHourRow[]): boolean {
  const deduped = dedupePayrollVisitsByPair(visits);
  const branchIds = new Set(
    deduped.map((v) => Number(v.branchId)).filter((id) => id > 0)
  );
  return branchIds.size > 1 || deduped.length > 1;
}

export function isVariosProgramadoLabel(entrada?: string, salida?: string): boolean {
  return (
    String(entrada || "").trim() === "Varios" ||
    String(salida || "").trim() === "Varios"
  );
}

/** Programado mostrado = checada (día no programado en pantalla). */
export function programadoMirrorsRegistrado(row: {
  entradaProgramada?: string;
  salidaProgramada?: string;
  entradaRegistrada?: string;
  salidaRegistrada?: string;
}): boolean {
  const entradaProgramada = String(row.entradaProgramada || "").trim();
  const salidaProgramada = String(row.salidaProgramada || "").trim();
  if (
    !entradaProgramada ||
    entradaProgramada === "N/A" ||
    isVariosProgramadoLabel(entradaProgramada, salidaProgramada)
  ) {
    return false;
  }
  return (
    entradaProgramada === String(row.entradaRegistrada || "").trim() &&
    salidaProgramada === String(row.salidaRegistrada || "").trim()
  );
}

export function shouldAlignUnscheduledPayrollHours(
  schedules: Array<{ start?: string; end?: string; startTime?: string; endTime?: string }>,
  visits: PayrollVisitHourRow[],
  row?: {
    entradaProgramada?: string;
    salidaProgramada?: string;
    entradaRegistrada?: string;
    salidaRegistrada?: string;
  }
): boolean {
  return (
    isUnscheduledPayrollDay(schedules, visits) ||
    Boolean(row && programadoMirrorsRegistrado(row))
  );
}

/** Alinea horas cuando el día es no programado (sin turno en agenda). */
export function normalizeUnscheduledDetailedRow<
  T extends {
    fecha: string;
    entradaRegistrada?: string;
    salidaRegistrada?: string;
    horasRegistradas?: number;
    horasTrabajadasEnHorario?: number;
    horasExtras?: number;
    visits?: PayrollVisitHourRow[];
  },
>(
  row: T,
  schedules: Array<{ start?: string; end?: string; startTime?: string; endTime?: string }>,
  options?: { nightScheduleEnabled?: boolean }
): T {
  const visits: PayrollVisitHourRow[] =
    row.visits && row.visits.length > 0
      ? dedupePayrollVisitsByPair(row.visits)
      : row.entradaRegistrada && row.salidaRegistrada
        ? [
            {
              dateKey: row.fecha,
              fecha: row.fecha,
              entradaRegistrada: row.entradaRegistrada,
              salidaRegistrada: row.salidaRegistrada,
              horasRegistradas: Number(row.horasRegistradas ?? 0),
            },
          ]
        : [];

  if (!shouldAlignUnscheduledPayrollHours(schedules, visits, row)) {
    return row;
  }

  const hours = resolveUnscheduledPayrollDayHours(visits, {
    entradaRegistrada: row.entradaRegistrada,
    salidaRegistrada: row.salidaRegistrada,
    nightScheduleEnabled: options?.nightScheduleEnabled,
  });
  return {
    ...row,
    horasRegistradas: hours.horasRegistradas,
    horasTrabajadasEnHorario: hours.horasTrabajadasEnHorario,
    horasExtras: hours.horasExtras,
  };
}
